import { AutoCompleteProps, Checkbox, Initials, Typography, Tooltip, Badge } from "@kaltura/ds-react-components";
import { HTMLAttributes, useState } from "react";
import { AutocompleteRenderGetTagProps } from "@mui/material";
import { Close16Icon } from "@kaltura/ds-react-icons";
import throttle from "lodash/throttle";
import {
    StyledAutocomplete,
    StyledAvatarImage,
    StyledChip,
    StyledInitialsWrap,
    StyledLi,
    StyledTypography,
} from "./styled-components";
import { Collaborator } from "./collaborator";
import { getKmsData, translate } from "@kaltura/mediaspace-shared-utils";

/**
 * component props
 */
export interface UserSelectionProps
    extends Omit<AutoCompleteProps, "freeSolo" | "renderOption" | "renderTags" | "options"> {
    url: string;
    /** Hide selected render tags from the input field (no chips will be displayed) */
    hideRenderTags?: boolean;
    /** context where the user suggestions request occurs */
    context?: string;
}

/**
 * user avatar - thumbnail url if exists, initials otherwise
 */
const Avatar = (props: Collaborator) => {
    return props.thumbnailUrl ? (
        <StyledAvatarImage src={props.thumbnailUrl} alt="" />
    ) : (
        <StyledInitialsWrap>
            {props.isGroup ? (
                <Initials fullName={props.screenName} size={"small"} />
            ) : (
                <Initials firstName={props.firstName} lastName={props.lastName} size={"small"} />
            )}
        </StyledInitialsWrap>
    );
};

/**
 * custom options renderer
 * @param props
 * @param option
 * @param selected
 */
const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: Collaborator,
    { selected }: { selected: boolean }
) => {
    return (
        <StyledLi {...props} key={option.id}>
            <Checkbox checked={selected} />
            <Avatar {...option} />
            <Typography variant={"body2Highlight"}>{option.screenName}</Typography>
            {option.isGroup && <Badge label={translate("Group")} size="small" />}
            {option.additionalLabel && <StyledTypography>{option.additionalLabel}</StyledTypography>}
        </StyledLi>
    );
};

/**
 * custom selected options renderer
 * @param tagValue
 * @param getTagProps
 */
const renderTags = (tagValue: Collaborator[], getTagProps: AutocompleteRenderGetTagProps) => {
    return tagValue.map((option: Collaborator, index: number) => {
        const { key, ...tagProps } = getTagProps({ index });

        const label = option.isGroup ? (
            <>
                {option.screenName} <Badge label={translate("Group")} size="small" />
            </>
        ) : (
            option.screenName
        );

        if (option.additionalLabel) {
            return (
                <Tooltip key={key} title={option.additionalLabel} placement="top">
                    <StyledChip
                        avatar={<Avatar {...option} />}
                        deleteIcon={<Close16Icon />}
                        label={label}
                        {...tagProps}
                    />
                </Tooltip>
            );
        }
        else {
            return (
                <StyledChip
                    key={key}
                    avatar={<Avatar {...option} />}
                    deleteIcon={<Close16Icon />}
                    label={label}
                    {...tagProps}
                />
            );
        }
    });
};

/**
 * user search component
 */
export function UserSelection({ url, hideRenderTags, context, ...props }: UserSelectionProps) {
    const [value, setValue] = useState<Collaborator[]>([]);
    const [options, setOptions] = useState<Collaborator[]>([]);
    const [loading, setLoading] = useState(false);

    const handleInputchange = throttle(
        async (inputValue: string) => {
            if (inputValue.length < 3) {
                setOptions(value ? value : []);
                return undefined;
            }
            setLoading(true);

            try {
                const results = await getKmsData(
                    url + encodeURIComponent(inputValue) + (context ? `/context/${context}` : "")
                );

                let newOptions: Collaborator[] = [];
                if (value) {
                    newOptions = value;
                }
                if (results) {
                    newOptions = [...newOptions, ...results];
                }
                setOptions(newOptions);
            }
            catch (e) {
                console.debug("error fetching user suggestions", e);
                setOptions(value ? value : []);
            }
            setLoading(false);
        },
        500,
        { leading: false }
    );

    return (
        <StyledAutocomplete
            {...props}
            loading={loading}
            getOptionLabel={(option: Collaborator) => option.screenName}
            options={options}
            renderOption={renderOption}
            renderTags={hideRenderTags ? () => [] : renderTags}
            isOptionEqualToValue={(option: Collaborator, value: Collaborator) => {
                return option.id === value.id;
            }}
            fullWidth={true}
            onKeyDown={(event) => {
                // when autoComplete input is rendered from an open mui menu
                // and there's a menu item that starts with the entered character, the menu item will be focused,
                // so the autocomplete input will lose focus and the character won't be added to the input's value
                // in order to prevent this behaviour stopPropagation() should be used
                event.stopPropagation();
            }}
            onInputChange={(event, newInputValue, reason) => {
                // only fetch new options when the user is actively typing.
                // when selecting an option, MUI clears the input and fires onInputChange
                // with reason "selectOption", clearing the options list,
                // and forcing the user to search again.
                // allow selecting other users from the same results
                if (reason === "input") {
                    handleInputchange(newInputValue);
                }
                props.onInputChange?.(event, newInputValue, reason);
            }}
            // disable MUI client-side filtering - show all returned results
            // the filtering is done by the KMS server
            filterOptions={(options) => options}
        />
    );
}
